import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { OAuthButtons } from "@/components/OAuthButtons";
import { getStoredReferralCode, clearStoredReferralCode } from "@/lib/referral";
import { readPendingCartAdd } from "@/hooks/useCart";
import { isValidRealName, REAL_NAME_MESSAGE } from "@shared/names";

const registerSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, "First name is required")
      .refine(isValidRealName, REAL_NAME_MESSAGE),
    lastName: z
      .string()
      .trim()
      .min(1, "Last name is required")
      .refine(isValidRealName, REAL_NAME_MESSAGE),
    email: z.string().email("Enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(8, "Please confirm your password"),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

export default function Register() {
  // Prefill the email when arriving from an express-checkout receipt link
  // (/register?email=...), so a guest buyer claims the right account.
  const prefillEmail =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("email") || ""
      : "";
  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: prefillEmail,
      password: "",
      confirmPassword: "",
    },
  });
  const [, setLocation] = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when a logged-out tap on "Add to cart" sent them here via /login.
  const [hasPendingCartAdd] = useState(() => readPendingCartAdd() != null);

  const onSubmit = async (values: RegisterFormValues) => {
    setSubmitError(null);
    try {
      const response = await apiRequest("POST", "/api/register", {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        referralCode: getStoredReferralCode() || undefined,
      });
      const user = await response.json();
      clearStoredReferralCode();
      queryClient.setQueryData(["/api/auth/user"], user);
      // Email/password signups must confirm their address before onboarding.
      setLocation("/check-email");
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Unable to register right now. Please try again.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex items-center justify-center">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader>
          <CardTitle>Create your account</CardTitle>
          <CardDescription>
            {hasPendingCartAdd
              ? "Create an account to add that to your cart. We saved it for you."
              : "Join the neighborhood marketplace in just a few steps."}
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <OAuthButtons label="Sign up" />
              </div>
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Create a password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Repeat your password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {submitError && (
                <p className="md:col-span-2 text-sm text-destructive">{submitError}</p>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3 items-stretch">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Creating account…" : "Create Account"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                By creating an account, you agree to our{" "}
                <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="underline">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">
                  Privacy Policy
                </a>
                .
              </p>
              <p className="text-sm text-muted-foreground text-center">
                Already have an account?{" "}
                <Link href="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  );
}
